import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Activity, TrendingUp, TrendingDown } from "lucide-react";
import TradingViewWidget from "@/components/TradingViewWidget";

/* Simulated session change — purely visual, not a real quote. Each
   asset gets a stable pseudo-random change between -1.5% and +2.0%
   so the user sees a believable green/red move per card. */
const ASSETS = [
  { id: "EURUSD", label: "EUR / USD",     symbol: "FX:EURUSD",      categoryKey: "markets.cat.fx",      change: +0.42 },
  { id: "XAUUSD", label: "Gold",          symbol: "OANDA:XAUUSD",   categoryKey: "markets.cat.metals",  change: +1.18 },
  { id: "BTCUSD", label: "Bitcoin",       symbol: "BINANCE:BTCUSDT",categoryKey: "markets.cat.crypto",  change: -0.92 },
  { id: "NAS100", label: "Nasdaq 100",    symbol: "OANDA:NAS100USD",categoryKey: "markets.cat.indices", change: +0.67 },
  { id: "USOIL",  label: "WTI Crude",     symbol: "TVC:USOIL",      categoryKey: "markets.cat.energy",  change: -1.34 },
  { id: "GBPJPY", label: "GBP / JPY",     symbol: "FX:GBPJPY",      categoryKey: "markets.cat.fx",      change: +0.21 },
];

function ChangePill({ change }) {
  const up = change >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  const color = up ? "#54d97c" : "#f06a6a";
  const bg = up ? "rgba(80,220,130,0.12)" : "rgba(240,106,106,0.12)";
  const border = up ? "rgba(80,220,130,0.32)" : "rgba(240,106,106,0.32)";
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[12px] font-semibold tabular-nums"
      style={{ background: bg, border: `1px solid ${border}`, color }}
      data-testid="markets-change-pill"
    >
      <Icon size={10} strokeWidth={2.6} />
      {up ? "+" : ""}{change.toFixed(2)}%
    </span>
  );
}

export default function LiveMarketsPanel() {
  const { t } = useTranslation();
  const [active, setActive] = useState(ASSETS[0]);
  const others = useMemo(() => ASSETS.filter((a) => a.id !== active.id).slice(0, 4), [active.id]);

  // Trend-tinted glow for the active chart border + ticker rail.
  const up = active.change >= 0;
  const trendColor = up ? "80, 220, 130" : "240, 90, 90";    // green / red RGB
  const trendGlow = `0 0 0 1px rgba(${trendColor}, 0.32), 0 0 24px rgba(${trendColor}, 0.18)`;

  return (
    <section id="markets" data-testid="live-markets-section" className="relative py-14 lg:py-28">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-7">
          <div>
            <div className="pill-mono inline-flex mb-3">
              <Activity size={11} className="mr-1.5 -ml-1" /> {t("markets.pill", "Live Markets")}
            </div>
            <h3 className="font-display text-2xl lg:text-4xl font-medium tracking-tight text-[color:var(--text-1)]">
              {t("markets.title", "Real-time charts across")}{" "}
              <span className="text-gradient-brand font-semibold">{t("markets.titleAccent", "every asset class")}</span>
            </h3>
          </div>
          <p className="text-[color:var(--text-3)] text-sm font-light max-w-md">
            {t("markets.subtitle", "FX, metals, crypto, indices and energy — switch instruments instantly. Trade similar pairs once funded.")}
          </p>
        </div>

        {/* Asset tabs */}
        <div
          data-testid="markets-tabs"
          className="flex gap-1.5 overflow-x-auto pb-2 mb-4 -mx-1 px-1 scrollbar-thin"
        >
          {ASSETS.map((a) => {
            const isActive = a.id === active.id;
            return (
              <button
                key={a.id}
                onClick={() => setActive(a)}
                data-testid={`markets-tab-${a.id}`}
                className={`group h-10 inline-flex items-center gap-2 px-4 rounded-lg text-sm whitespace-nowrap border transition-all
                  ${isActive
                    ? "border-brand/60 text-[color:var(--text-1)] bg-[color:var(--surface-2)]"
                    : "border-[color:var(--line)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)] hover:border-[color:var(--line-strong)]"}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-brand animate-pulse" : "bg-[color:var(--text-3)]"}`} />
                <span className="font-medium">{a.label}</span>
                <ChangePill change={a.change} />
              </button>
            );
          })}
        </div>

        {/* Primary chart — bordered with the current trend colour so the
            chart literally pulses green on green moves and red on red. */}
        <div
          className="card-beam overflow-hidden relative transition-shadow duration-500"
          style={{ lineHeight: 0, fontSize: 0, boxShadow: trendGlow }}
          data-testid="markets-primary-chart"
        >
          {/* Tiny live ticker rail across the top of the chart card */}
          <div
            className="absolute top-0 left-0 right-0 z-10 flex items-center gap-2 px-3 py-1.5 backdrop-blur-sm"
            style={{
              background: `linear-gradient(90deg, rgba(${trendColor},0.10), rgba(0,0,0,0) 60%)`,
              borderBottom: `1px solid rgba(${trendColor},0.22)`,
              lineHeight: 1,
              fontSize: "10px",
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full animate-pulse shrink-0"
              style={{ background: `rgb(${trendColor})`, boxShadow: `0 0 8px rgb(${trendColor})` }}
            />
            <span className="font-mono uppercase tracking-[0.18em]" style={{ color: `rgb(${trendColor})` }}>
              {t("markets.live", "LIVE")} · {active.label}
            </span>
            <span className="ml-auto inline-flex items-center gap-1 font-semibold tabular-nums" style={{ color: `rgb(${trendColor})` }}>
              {up ? <TrendingUp size={10} strokeWidth={2.6} /> : <TrendingDown size={10} strokeWidth={2.6} />}
              {up ? "+" : ""}{active.change.toFixed(2)}%
            </span>
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ lineHeight: 0, fontSize: 0, display: "block" }}
            >
              <TradingViewWidget symbol={active.symbol} height={580} />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Mini charts strip — 4 alternates, each tinted by its own trend */}
        <div className="hidden md:grid mt-5 grid-cols-2 lg:grid-cols-4 gap-3" data-testid="markets-mini-strip">
          {others.map((a) => {
            const mUp = a.change >= 0;
            const mRgb = mUp ? "80, 220, 130" : "240, 90, 90";
            return (
            <button
              key={a.id}
              onClick={() => setActive(a)}
              data-testid={`markets-mini-${a.id}`}
              className="card-beam overflow-hidden text-left transition-all hover:translate-y-[-2px]"
              style={{ boxShadow: `0 0 0 1px rgba(${mRgb},0.18), 0 0 14px rgba(${mRgb},0.08)` }}
            >
              <div className="px-3 py-2 flex items-center justify-between" style={{ borderBottom: `1px solid rgba(${mRgb},0.18)` }}>
                <div>
                  <div className="text-sm font-medium text-[color:var(--text-1)]">{a.label}</div>
                  <div className="font-mono text-[10px] text-[color:var(--text-3)] uppercase tracking-widest">{t(a.categoryKey)}</div>
                </div>
                <ChangePill change={a.change} />
              </div>
              <div className="border-t border-[color:var(--line)]" style={{ lineHeight: 0, fontSize: 0 }}>
                <TradingViewWidget symbol={a.symbol} height={180} mini />
              </div>
            </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
